import { useState, useEffect } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface ExpenseCategory {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  sort_order: number;
}

interface OperatingExpense {
  id: string;
  category_id: string;
  category_name: string;
  description: string | null;
  amount: number;
  month: number;
  year: number;
  is_recurring: boolean;
}

interface MonthlyExpenseSummary {
  year: number;
  month: number;
  total_amount: number;
  expense_count: number;
  recurring_count: number;
  expenses: OperatingExpense[];
}

interface YearlySummary {
  year: number;
  months: Array<{
    month: number;
    total_amount: number;
    expense_count: number;
  }>;
  year_total: number;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

async function apiRequest(endpoint: string, options: RequestInit = {}) {
  const token = localStorage.getItem('admin_auth_token');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }
  if (response.status === 204) return null;
  return response.json();
}

export function OperatingCostsPage() {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [monthlyData, setMonthlyData] = useState<MonthlyExpenseSummary | null>(null);
  const [yearlyData, setYearlyData] = useState<YearlySummary | null>(null);
  const [editingExpenses, setEditingExpenses] = useState<Record<string, { amount: string; is_recurring: boolean; description: string }>>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'monthly' | 'yearly'>('monthly');

  // Category management
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const [savingCategory, setSavingCategory] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'monthly') {
      loadMonthlyData();
    } else {
      loadYearlyData();
    }
  }, [selectedMonth, selectedYear, activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [cats] = await Promise.all([
        apiRequest('/cost-accounting/expense-categories'),
      ]);
      setCategories(cats);
      await loadMonthlyData();
    } catch (error) {
      console.error('Failed to load data:', error);
      setMessage({ type: 'error', text: 'Failed to load expense data' });
    } finally {
      setLoading(false);
    }
  };

  const loadMonthlyData = async () => {
    try {
      const data = await apiRequest(`/cost-accounting/expenses/${selectedYear}/${selectedMonth}`);
      setMonthlyData(data);

      // Initialize editing state
      const editState: Record<string, { amount: string; is_recurring: boolean; description: string }> = {};
      for (const cat of categories) {
        const expense = data.expenses.find((e: OperatingExpense) => e.category_id === cat.id);
        editState[cat.id] = {
          amount: expense ? expense.amount.toString() : '',
          is_recurring: expense ? expense.is_recurring : false,
          description: expense ? expense.description || '' : '',
        };
      }
      setEditingExpenses(editState);
    } catch (error) {
      console.error('Failed to load monthly data:', error);
    }
  };

  const loadYearlyData = async () => {
    try {
      const data = await apiRequest(`/cost-accounting/expenses/${selectedYear}`);
      setYearlyData(data);
    } catch (error) {
      console.error('Failed to load yearly data:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const expenses = Object.entries(editingExpenses).map(([category_id, data]) => ({
        category_id,
        amount: parseFloat(data.amount) || 0,
        is_recurring: data.is_recurring,
        description: data.description || undefined,
      }));

      await apiRequest(`/cost-accounting/expenses/bulk/${selectedYear}/${selectedMonth}`, {
        method: 'PUT',
        body: JSON.stringify({
          month: selectedMonth,
          year: selectedYear,
          expenses,
        }),
      });

      setMessage({ type: 'success', text: 'Expenses saved successfully!' });
      await loadMonthlyData();
    } catch (error) {
      console.error('Failed to save expenses:', error);
      setMessage({ type: 'error', text: 'Failed to save expenses' });
    } finally {
      setSaving(false);
    }
  };

  const handleCopyRecurring = async () => {
    // Get previous month
    let fromMonth = selectedMonth - 1;
    let fromYear = selectedYear;
    if (fromMonth === 0) {
      fromMonth = 12;
      fromYear--;
    }

    try {
      const result = await apiRequest('/cost-accounting/expenses/copy-recurring', {
        method: 'POST',
        body: JSON.stringify({
          from_month: fromMonth,
          from_year: fromYear,
          to_month: selectedMonth,
          to_year: selectedYear,
        }),
      });

      setMessage({ type: 'success', text: result.message });
      await loadMonthlyData();
    } catch (error) {
      console.error('Failed to copy recurring expenses:', error);
      setMessage({ type: 'error', text: 'Failed to copy recurring expenses' });
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) return;

    setSavingCategory(true);
    try {
      await apiRequest('/cost-accounting/expense-categories', {
        method: 'POST',
        body: JSON.stringify(newCategory),
      });

      setNewCategory({ name: '', description: '' });
      setShowCategoryModal(false);
      await loadData();
      setMessage({ type: 'success', text: 'Category created successfully!' });
    } catch (error) {
      console.error('Failed to create category:', error);
      setMessage({ type: 'error', text: 'Failed to create category' });
    } finally {
      setSavingCategory(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6">
      <PageHeader
        title="Operating Costs"
        subtitle="Track monthly operating expenses for overhead allocation"
      />

      {/* Message Banner */}
      {message && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          {message.text}
          <button
            onClick={() => setMessage(null)}
            className="float-right text-gray-500 hover:text-gray-700"
          >
            ×
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-4">
          {[
            { id: 'monthly', label: 'Monthly Entry' },
            { id: 'yearly', label: 'Yearly Summary' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as 'monthly' | 'yearly')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'monthly' && (
        <div className="space-y-6">
          {/* Month/Year Selector */}
          <Card>
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="border border-gray-300 rounded-lg px-3 py-2 bg-white"
                >
                  {MONTH_NAMES.map((name, index) => (
                    <option key={index} value={index + 1}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="border border-gray-300 rounded-lg px-3 py-2 bg-white"
                >
                  {[2024, 2025, 2026, 2027, 2028].map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1" />
              <button
                onClick={handleCopyRecurring}
                className="px-4 py-2 text-sm text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50"
              >
                Copy Recurring from Previous Month
              </button>
              <button
                onClick={() => setShowCategoryModal(true)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                + Add Category
              </button>
            </div>
          </Card>

          {/* Monthly Total */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-semibold text-blue-800">
                  {MONTH_NAMES[selectedMonth - 1]} {selectedYear} Total
                </h3>
                <p className="text-sm text-blue-600">
                  {monthlyData?.expense_count || 0} expenses | {monthlyData?.recurring_count || 0} recurring
                </p>
              </div>
              <div className="text-3xl font-bold text-blue-900">
                {formatCurrency(monthlyData?.total_amount || 0)}
              </div>
            </div>
          </div>

          {/* Expense Entry Table */}
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Category</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 w-32">Amount</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-700 w-24">Recurring</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {categories.map((category) => (
                    <tr key={category.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{category.name}</div>
                        {category.description && (
                          <div className="text-xs text-gray-500">{category.description}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editingExpenses[category.id]?.amount || ''}
                            onChange={(e) =>
                              setEditingExpenses({
                                ...editingExpenses,
                                [category.id]: {
                                  ...editingExpenses[category.id],
                                  amount: e.target.value,
                                },
                              })
                            }
                            onWheel={(e) => e.currentTarget.blur()}
                            className="w-full border border-gray-300 rounded-lg pl-7 pr-3 py-2"
                            placeholder="0.00"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={editingExpenses[category.id]?.is_recurring || false}
                          onChange={(e) =>
                            setEditingExpenses({
                              ...editingExpenses,
                              [category.id]: {
                                ...editingExpenses[category.id],
                                is_recurring: e.target.checked,
                              },
                            })
                          }
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={editingExpenses[category.id]?.description || ''}
                          onChange={(e) =>
                            setEditingExpenses({
                              ...editingExpenses,
                              [category.id]: {
                                ...editingExpenses[category.id],
                                description: e.target.value,
                              },
                            })
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          placeholder="Optional notes..."
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex justify-end border-t border-gray-200 pt-4">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300"
              >
                {saving ? 'Saving...' : 'Save Expenses'}
              </button>
            </div>
          </Card>

          {/* Info Box */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-800 mb-2">About Operating Costs</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Operating costs are used to calculate overhead allocation per screen</li>
              <li>• Mark expenses as "Recurring" to easily copy them to the next month</li>
              <li>• Total monthly costs are divided by screens produced to get overhead per unit</li>
              <li>• Use notes to add context like invoice numbers or vendor names</li>
            </ul>
          </div>
        </div>
      )}

      {activeTab === 'yearly' && (
        <div className="space-y-6">
          {/* Year Selector */}
          <Card>
            <div className="flex items-center gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="border border-gray-300 rounded-lg px-3 py-2 bg-white"
                >
                  {[2024, 2025, 2026, 2027, 2028].map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1" />
              <div className="text-right">
                <div className="text-sm text-gray-500">Year Total</div>
                <div className="text-2xl font-bold text-gray-900">
                  {formatCurrency(yearlyData?.year_total || 0)}
                </div>
              </div>
            </div>
          </Card>

          {/* Monthly Breakdown Table */}
          <Card>
            <h3 className="font-semibold text-gray-900 mb-4">Monthly Breakdown</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Month</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Expenses</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Total</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 w-1/3">Distribution</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {yearlyData?.months.map((month) => {
                    const maxAmount = Math.max(...(yearlyData?.months.map((m) => m.total_amount) || [1]));
                    const percentage = maxAmount > 0 ? (month.total_amount / maxAmount) * 100 : 0;

                    return (
                      <tr
                        key={month.month}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          setSelectedMonth(month.month);
                          setActiveTab('monthly');
                        }}
                      >
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {MONTH_NAMES[month.month - 1]}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">
                          {month.expense_count}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900">
                          {formatCurrency(month.total_amount)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-blue-500 rounded-full transition-all"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-500 w-12">
                              {yearlyData?.year_total
                                ? Math.round((month.total_amount / yearlyData.year_total) * 100)
                                : 0}
                              %
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                  <tr>
                    <td className="px-4 py-3 font-semibold text-gray-900">Total</td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      {yearlyData?.months.reduce((sum, m) => sum + m.expense_count, 0)}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-gray-900">
                      {formatCurrency(yearlyData?.year_total || 0)}
                    </td>
                    <td className="px-4 py-3" />
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>

          {/* Average Stats */}
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <div className="text-center">
                <div className="text-sm text-gray-500">Monthly Average</div>
                <div className="text-2xl font-bold text-gray-900">
                  {formatCurrency((yearlyData?.year_total || 0) / 12)}
                </div>
              </div>
            </Card>
            <Card>
              <div className="text-center">
                <div className="text-sm text-gray-500">Highest Month</div>
                <div className="text-2xl font-bold text-gray-900">
                  {formatCurrency(Math.max(...(yearlyData?.months.map((m) => m.total_amount) || [0])))}
                </div>
              </div>
            </Card>
            <Card>
              <div className="text-center">
                <div className="text-sm text-gray-500">Lowest Month</div>
                <div className="text-2xl font-bold text-gray-900">
                  {formatCurrency(
                    Math.min(
                      ...(yearlyData?.months.filter((m) => m.total_amount > 0).map((m) => m.total_amount) || [0])
                    ) || 0
                  )}
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Add Expense Category</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category Name *</label>
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="e.g., Security Services"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={newCategory.description}
                  onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Optional description"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCategoryModal(false);
                  setNewCategory({ name: '', description: '' });
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCategory}
                disabled={savingCategory || !newCategory.name.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300"
              >
                {savingCategory ? 'Creating...' : 'Create Category'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
